import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History, RotateCcw, Download } from 'lucide-react';
import { format } from 'date-fns';

export default function ArtifactVersionHistory({ artifact, officers, open, onClose }) {
  const queryClient = useQueryClient();
  const [selectedVersion, setSelectedVersion] = useState(null);

  const { data: versions = [] } = useQuery({
    queryKey: ['artifactVersions', artifact?.id],
    queryFn: async () => {
      const allVersions = await base44.entities.ArtifactVersion.list();
      return allVersions
        .filter((v) => v.artifact_id === artifact.id)
        .sort((a, b) => b.version_number - a.version_number);
    },
    enabled: !!artifact?.id && open
  });

  const { data: documents = [] } = useQuery({
    queryKey: ['artifactDocuments', artifact?.id],
    queryFn: async () => {
      const allDocs = await base44.entities.Document.list();
      return allDocs.filter(doc =>
        doc.context_type === 'Artifact' &&
        doc.parent_context_id === artifact.id
      );
    },
    enabled: !!artifact?.id && open
  });

  const getFileSizeForUrl = (fileUrl) => {
    if (!fileUrl) return null;
    // Match by drive_web_link or by checking if the URL contains the same drive file ID
    const doc = documents.find(d => 
      d.drive_web_link === fileUrl || 
      (d.drive_file_id && fileUrl?.includes(d.drive_file_id))
    );
    return doc?.file_size || null;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const revertMutation = useMutation({
    mutationFn: async (versionId) => {
      const version = versions.find((v) => v.id === versionId);
      if (!version) throw new Error('Version not found');

      // Create a new version from current state
      await base44.entities.ArtifactVersion.create({
        artifact_id: artifact.id,
        version_number: versions[0]?.version_number + 1 || 1,
        name: artifact.name,
        description: artifact.description,
        category: artifact.category,
        type: artifact.type,
        file_url: artifact.file_url,
        internal_page: artifact.internal_page,
        tags: artifact.tags,
        editors: artifact.editors,
        viewers: artifact.viewers,
        owner_email: artifact.owner_email,
        status: artifact.status,
        changed_by: (await base44.auth.me()).email,
        change_description: `Reverted to version ${version.version_number}`
      });

      // Update artifact to match the selected version
      return base44.entities.Artifact.update(artifact.id, {
        name: version.name,
        description: version.description,
        category: version.category,
        type: version.type,
        file_url: version.file_url,
        internal_page: version.internal_page,
        tags: version.tags,
        editors: version.editors,
        viewers: version.viewers
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['artifactVersions'] });
      setSelectedVersion(null);
    }
  });

  const handleDownloadVersion = async (version) => {
    if (version.file_url) {
      window.open(version.file_url, '_blank');
    } else {
      alert('No file attached to this version.');
    }
  };

  const handleDownloadCurrent = async () => {
    if (artifact.file_url) {
      window.open(artifact.file_url, '_blank');
    } else {
      alert('No file attached to this artifact.');
    }
  };

  const getChangedFields = (current, previous) => {
    if (!previous) return [];
    const changes = [];
    
    if (current.name !== previous.name) changes.push('name');
    if (current.description !== previous.description) changes.push('description');
    if (current.category !== previous.category) changes.push('category');
    if (current.type !== previous.type) changes.push('type');
    if (current.file_url !== previous.file_url) changes.push('file');
    if (JSON.stringify(current.tags) !== JSON.stringify(previous.tags)) changes.push('tags');
    if (JSON.stringify(current.editors) !== JSON.stringify(previous.editors)) changes.push('editors');
    if (JSON.stringify(current.viewers) !== JSON.stringify(previous.viewers)) changes.push('viewers');
    
    return changes;
  };

  const renderFieldComparison = (label, currentValue, previousValue) => {
    const isDifferent = JSON.stringify(currentValue) !== JSON.stringify(previousValue);
    
    if (!isDifferent) return null;

    return (
      <div className="space-y-1">
        <div className="text-xs font-semibold text-slate-400">{label}</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs">
            <div className="text-red-400 font-semibold mb-1">Previous</div>
            <div className="text-slate-300">
              {Array.isArray(previousValue) ? previousValue.join(', ') || '(none)' : previousValue || '(none)'}
            </div>
          </div>
          <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs">
            <div className="text-emerald-400 font-semibold mb-1">Current</div>
            <div className="text-slate-300">
              {Array.isArray(currentValue) ? currentValue.join(', ') || '(none)' : currentValue || '(none)'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="art-artifactversionhistory-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-400" />
            Version History: {artifact?.name}
          </DialogTitle>
        </DialogHeader>

        {versions.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <History className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No version history yet</p>
            <p className="text-sm text-slate-500 mt-2">Versions will be created when you edit this artifact</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(100,116,139,0.15) transparent' }}>
            <div className="space-y-3">
              {/* Version History */}
              {versions.map((version, idx) => {
                const changedBy = officers.find((o) => o.user_email === version.changed_by);
                const previousVersion = versions[idx + 1];
                const changedFields = getChangedFields(version, previousVersion);
                const isCurrent = idx === 0;
                return (
                  <Card key={version.id} className={isCurrent ? "bg-indigo-500/10 border-indigo-500/30" : "bg-slate-950 border-slate-800"}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {isCurrent && (
                              <Badge className="bg-indigo-600 text-white font-semibold border-none">Current</Badge>
                            )}
                            <Badge variant="outline" className={`text-xs ${isCurrent ? 'border-indigo-400 text-indigo-200' : 'border-slate-500 text-slate-200'}`}>
                              Version {version.version_number}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {format(new Date(version.created_date), 'MMM d, yyyy h:mm a')}
                            </span>
                            <span className="text-xs text-slate-500">
                              by {changedBy?.callsign || version.changed_by}
                            </span>
                            {formatFileSize(getFileSizeForUrl(version.file_url)) && (
                              <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                                {formatFileSize(getFileSizeForUrl(version.file_url))}
                              </Badge>
                            )}
                          </div>
                          {version.change_description && (
                            <p className="text-sm text-slate-400 italic mb-2">"{version.change_description}"</p>
                          )}
                          {changedFields.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {changedFields.map((field) => (
                                <Badge key={field} variant="secondary" className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                  {field}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => isCurrent ? handleDownloadCurrent() : handleDownloadVersion(version)}
                            className={`h-8 text-xs ${isCurrent ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/20 hover:text-indigo-200' : 'border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-slate-200'}`}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                          {!isCurrent && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => revertMutation.mutate(version.id)}
                              disabled={revertMutation.isPending}
                              className="h-8 text-xs border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Revert
                            </Button>
                          )}
                        </div>
                      </div>


                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}